import ResponsiveContent from "../../../layout/ResponsiveContent";
import React, {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {capitalize} from "lodash";
import {
  useGetHazardParametersQuery,
  useGetHazardTypeClassesQuery,
  usePatchHazardParametersBulkMutation,
} from "../../../redux/api/hazardTypes";
import {Button, Flex, Modal, Space, Table, Tag, Typography} from "antd";
import {HazardParameter} from "../../../types/internal/Hazard";
import {ColumnsType} from "antd/lib/table";
import ImpactSettingsEdit from "./ImpactSettingsEdit";
import HelpTooltip from "../../common/HelpTooltip";
import {VALUE_NOT_PRESENT} from "../../../utils/translation";
import {useMessageContext} from "../../../context/message/MessageContext";
import {useAsyncProcessHazardParameters} from "../../../customHooks/useAsyncProcessHazardParameters";
import {apiSlice} from "../../../redux/api/baseApi";
import {useDispatch} from "react-redux";

export interface ImpactItemType extends HazardParameter {
  label: string,
}

function Change(){
  return <span style={{color: "red"}}>*</span>
}

export default function ImpactSettings() {

  const {t} = useTranslation();
  const dispatch = useDispatch()
  const navigate = useNavigate();
  const setMessage = useMessageContext();
  const {
    data: hazardTypes,
    requestId: requestIdHazardTypes
  } = useGetHazardTypeClassesQuery();

  const {data: hazardParameters, isFetching, isUninitialized, requestId: requestIdHazardParameters} = useGetHazardParametersQuery()
  const [pendingChanges, setPendingChanges] = useState<ImpactItemType[]>([])
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [patchBulk, {isLoading: isPatchingBulk}] = usePatchHazardParametersBulkMutation();

  const getHazardType = useCallback((hazardTypeClass: string) => {
    return hazardTypes?.find(el => el.uuid === hazardTypeClass)
  }, [hazardTypes])

  const impactItems: ImpactItemType[] = useMemo(() => {
    return hazardParameters?.map(hazard => ({
      ...hazard,
      label: getHazardType(hazard.hazard_class)?.name ?? "",
    })).sort((a, b) => a.label.localeCompare(b.label)) ?? []
  }, [getHazardType, hazardParameters])

  const findChange = useMemo(() => (uuid: string) => {
    return pendingChanges.find(c => c.uuid === uuid)
  }, [pendingChanges])

  const handleEditFinish = useCallback((record: ImpactItemType) => (values: {
    magnitude: number;
    to_be_used_in_impact: boolean
  }) => {
    setPendingChanges(prev => {
      const isNotChanged = values.magnitude === record.magnitude && values.to_be_used_in_impact === record.to_be_used_in_impact
      if (isNotChanged) {
        return prev.filter(el => el.uuid !== record.uuid)
      }
      const idx = prev.findIndex(c => c.uuid === record.uuid);
      const updated: ImpactItemType = {...record, ...values};
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = updated;
        return next;
      }
      return [...prev, updated];
    });
  }, []);

  const [isUpdating, setIsUpdating] = useState(false)

  const {start} = useAsyncProcessHazardParameters({
    pollingInterval: 3000,
    defaultMessages: true,
    messages: {
      loading: capitalize(t('settings.impactParametersUpdateLoading')),
      success: capitalize(t('settings.impactParametersUpdateSuccess')),
      error: capitalize(t('settings.impactParametersUpdateError')),
    },
    onError: (error) => {
      console.error("Errore:", error)
      setIsUpdating(false)
    },
    onSuccess: () => {
      setPendingChanges([]);
      setIsUpdating(false)
      dispatch(apiSlice.util.invalidateTags(['HazardParameters', 'Asset', 'HazardTypes']));
    },
  });

  const handleConfirm = useCallback(async () => {
    try {
      setIsUpdating(true)
      const res = await patchBulk(pendingChanges.map(c => ({
        uuid: c.uuid,
        magnitude: c.magnitude,
        to_be_used_in_impact: c.to_be_used_in_impact,
      }))).unwrap()
      await start(res.uuid)
      setIsConfirmOpen(false);
    } catch {
      setIsUpdating(false)
      setMessage({error: capitalize(t('settings.impactParametersUpdateError'))});
    }
  }, [patchBulk, pendingChanges, setMessage, start, t]);

  const handleCancel = useCallback(() => {
    setIsConfirmOpen(false);
  }, []);

  const columns: ColumnsType<ImpactItemType> = useMemo(() => {
    return [
      {
        key: 'label',
        dataIndex: "label",
        title: capitalize(t('hazards.hazard_one')),
        render: (value, record) => {
          const isUpdating = !!findChange(record.uuid)
          return <>{isUpdating && <Change/>} {record.label}</>
        }
      },
      {
        key: 'magnitude',
        dataIndex: 'magnitude',
        title: <>
          {capitalize(t('hazards.vulnerability.magnitude'))}
          <HelpTooltip
            tooltip={{
              title: <Flex gap={'0.5rem'} vertical>
                <Typography.Text strong>{capitalize(t('hazards.vulnerability.magnitude'))}</Typography.Text>
                <Typography.Text>{(t('hazards.vulnerability.magnitudeDescription'))}</Typography.Text>
              </Flex>,
              color: 'white',
            }}
            iconStyle={{fontSize: '', marginLeft: '0.2rem'}}
          />
        </>,
        align: 'center',
        render: (value, record) => {

          let result = <></>
          switch (value) {
            case (-2):
              result = <>{VALUE_NOT_PRESENT}</>;
              break;
            case (-1):
              result = <>n.d.</>;
              break;
            default:
              result = <>{value}</>;
          }
          return result
        }
      },
      {
        key: 'to_be_used_in_impact',
        dataIndex: "to_be_used_in_impact",
        title: <>
          {capitalize(t('settings.impactVisible'))}
          <HelpTooltip
            tooltip={{
              title: <Flex gap={'0.5rem'} vertical>
                <Typography.Text>{t('settings.impactVisibleDescription')}</Typography.Text>
              </Flex>,
              color: 'white',
            }}
            iconStyle={{fontSize: '', marginLeft: '0.2rem'}}
          />
        </>,
        render: (value, record) => {
          return !!value && <Tag color={"geekblue"}>{capitalize(t('settings.impactVisible'))}</Tag>
        }
      },
      {
        key: 'actions',
        render: (_, record) => (
          <Space>
            <ImpactSettingsEdit buttonProps={{disabled: isUpdating}} impact={record} onFinish={handleEditFinish(record)}/>
          </Space>
        )
      }
    ]
  }, [findChange, handleEditFinish, isUpdating, t])


  return <ResponsiveContent
    title={t('settings.impact', {count: 2})}
    goBack={() => navigate('/settings')}
    goBackText={capitalize(t('settings.settings'))}
    extra={<Space style={{visibility: pendingChanges.length ? 'visible' : 'hidden'}}>
      <Button
        type={"primary"}
        onClick={() => setIsConfirmOpen(true)}>
        {capitalize(t('actions.confirmChanges'))}
      </Button>
    </Space>}
  >
    <Table
      key={(requestIdHazardParameters ?? "") + (requestIdHazardTypes ?? "")}
      rowKey={"uuid"}
      loading={isFetching || isUninitialized || isUpdating}
      columns={columns}
      dataSource={impactItems}
      pagination={false}
    />
    <Modal
      open={isConfirmOpen}
      onOk={handleConfirm}
      onCancel={handleCancel}
      confirmLoading={isPatchingBulk}
      title={capitalize(t('actions.confirmChanges'))}
      okText={capitalize(t('misc.yes'))}
      cancelText={capitalize(t('misc.no'))}
    >
      <Flex vertical gap={'0.75rem'}>
        <Typography.Text>
          {t('settings.impactParametersConfirmDescription')}
        </Typography.Text>
        <ul style={{paddingLeft: '1.25rem', margin: 0}}>
          {pendingChanges.map(change => (
            <li key={change.uuid}>
              <Typography.Text>
                <strong>{change.label}</strong>
                {' — '}
                {capitalize(t('hazards.vulnerability.magnitude'))}: <strong>{change.magnitude}</strong>
                {', '}
                {capitalize(t('settings.impactVisible'))}: <strong>{change.to_be_used_in_impact ? capitalize(t('misc.yes')) : capitalize(t('misc.no'))}</strong>
              </Typography.Text>
            </li>
          ))}
        </ul>
      </Flex>
    </Modal>
  </ResponsiveContent>
}
